import axios, { AxiosResponse } from "axios";
import { environment } from "../../environment";
import { useLogin } from "../login/login";
import {
    IAgenda, IAgendaEvent, IAppointment, IAppointmentNotice, ICalendarTeammate, IEventInput, IAvailabilityOverride, IAvailabilityPatch, IBookForLeadInput, ICrmError, ILaunchBotInput, IMeetingBot, IMeetingBotTranscript,
    DemoOutcome, IDemoWrapUp, IMeetingType, IMeetingTypePatch, IPendingWrapUp, ISchedulingSettings, ISlots, IWrapUpResult, IWrapUpSubmission
} from "./types";

const crmUrl = `${environment.v2Url}/crm`;

export type CrmSchedulingKeyFunctions = {
    settings: () => Promise<ISchedulingSettings | ICrmError>;
    updateSettings: (patch: IAvailabilityPatch) => Promise<ISchedulingSettings | ICrmError>;
    createMeetingType: (dto: IMeetingTypePatch) => Promise<IMeetingType | ICrmError>;
    updateMeetingType: (id: number, dto: IMeetingTypePatch) => Promise<IMeetingType | ICrmError>;
    listOverrides: (fromIso: string, toIso: string) => Promise<IAvailabilityOverride[] | ICrmError>;
    createOverride: (dto: { kind: 'block' | 'open'; start_at: string; end_at: string; note?: string | null }) => Promise<IAvailabilityOverride | ICrmError>;
    deleteOverride: (id: number) => Promise<true | ICrmError>;
    /** A week of someone's calendar: yours by default, or a teammate's. */
    agenda: (fromIso: string, toIso: string, userId?: number) => Promise<IAgenda | ICrmError>;
    /** Everyone on the sales team whose calendar can be shown, you first. */
    team: () => Promise<ICalendarTeammate[] | ICrmError>;
    createEvent: (input: IEventInput) => Promise<IAgendaEvent | ICrmError>;
    updateEvent: (calendarId: number, eventId: string, input: IEventInput) => Promise<IAgendaEvent | ICrmError>;
    deleteEvent: (calendarId: number, eventId: string, notify: boolean) => Promise<true | ICrmError>;
    /** The rep's own openings for a meeting type (no notice rule — they can book themselves in ten minutes). */
    slots: (meetingTypeId: number, fromIso: string, toIso: string, excludeAppointmentId?: number) => Promise<ISlots | ICrmError>;
    listAppointments: (fromIso: string, toIso: string) => Promise<IAppointment[] | ICrmError>;
    listForLead: (leadId: number) => Promise<IAppointment[] | ICrmError>;
    book: (input: IBookForLeadInput) => Promise<IAppointment | ICrmError>;
    cancel: (id: number, reason?: string | null) => Promise<IAppointment | ICrmError>;
    reschedule: (id: number, startIso: string) => Promise<IAppointment | ICrmError>;
    /** `sendFollowUp: false` skips the automatic thank-you / "sorry we missed you". */
    outcome: (id: number, status: 'completed' | 'no_show', sendFollowUp?: boolean) => Promise<IAppointment | ICrmError>;
    /** The rep heard from them another way. */
    confirmAppointment: (id: number) => Promise<IAppointment | ICrmError>;
    /** Reminders, nudges and after-call emails for one meeting. */
    notices: (id: number) => Promise<IAppointmentNotice[] | ICrmError>;
    /** Stop a queued after-call email inside its 15-minute undo window. */
    undoFollowUp: (id: number) => Promise<{ cancelled: number } | ICrmError>;
    /** Notetaker bots (Recall.ai): send one to an appointment or a bare meeting URL, watch it, take it away. */
    launchBot: (input: ILaunchBotInput) => Promise<IMeetingBot | ICrmError>;
    getBot: (id: number) => Promise<IMeetingBot | ICrmError>;
    botTranscript: (id: number) => Promise<IMeetingBotTranscript | ICrmError>;
    removeBot: (id: number) => Promise<IMeetingBot | ICrmError>;
    listBots: (appointmentId: number) => Promise<IMeetingBot[] | ICrmError>;
    /** The meeting wrap-up queue: analysed calls waiting for the rep's review. */
    pendingWrapUps: () => Promise<IPendingWrapUp[] | ICrmError>;
    completeWrapUp: (botId: number, submission: IWrapUpSubmission) => Promise<IWrapUpResult | ICrmError>;
    snoozeWrapUp: (botId: number, minutes?: number) => Promise<IPendingWrapUp | ICrmError>;
    skipWrapUp: (botId: number) => Promise<IPendingWrapUp | ICrmError>;
    reanalyzeBot: (botId: number) => Promise<IMeetingBot | ICrmError>;
    /** Finished Google Meet demos waiting for Held / No-show, and the rep's answer. */
    demoWrapUps: () => Promise<IDemoWrapUp[] | ICrmError>;
    confirmDemo: (eventId: string, outcome: DemoOutcome) => Promise<{ updated: number } | ICrmError>;
};

/** A rep's availability, meeting types, agenda and bookings. */
export const useCrmScheduling = (): [CrmSchedulingKeyFunctions] => {
    const [, authToken, loginFunctions] = useLogin();
    const headers = { 'X-Wallmates-Auth': authToken };

    const handle = (err: any): ICrmError => {
        const status = err?.response?.status ?? 0;
        if (status === 401) loginFunctions.logout();
        return {
            ok: false,
            status,
            message: err?.response?.data?.message
                || (status === 0 ? 'Could not reach Wallmates. Check your connection.' : 'Something went wrong.')
        };
    };

    const get = async <T>(path: string): Promise<T | ICrmError> => {
        try { const r: AxiosResponse<T> = await axios.get(`${crmUrl}${path}`, { headers }); return r.data; } catch (err: any) { return handle(err); }
    };
    const post = async <T>(path: string, body: unknown): Promise<T | ICrmError> => {
        try { const r: AxiosResponse<T> = await axios.post(`${crmUrl}${path}`, body, { headers }); return r.data; } catch (err: any) { return handle(err); }
    };
    const put = async <T>(path: string, body: unknown): Promise<T | ICrmError> => {
        try { const r: AxiosResponse<T> = await axios.put(`${crmUrl}${path}`, body, { headers }); return r.data; } catch (err: any) { return handle(err); }
    };
    const range = (fromIso: string, toIso: string) => `from=${encodeURIComponent(fromIso)}&to=${encodeURIComponent(toIso)}`;

    return [{
        settings: () => get<ISchedulingSettings>('/scheduling/settings'),
        updateSettings: (patch) => put<ISchedulingSettings>('/scheduling/settings', patch),
        createMeetingType: (dto) => post<IMeetingType>('/scheduling/meeting-types', dto),
        updateMeetingType: (id, dto) => put<IMeetingType>(`/scheduling/meeting-types/${id}`, dto),
        listOverrides: (f, t) => get<IAvailabilityOverride[]>(`/scheduling/overrides?${range(f, t)}`),
        createOverride: (dto) => post<IAvailabilityOverride>('/scheduling/overrides', dto),
        deleteOverride: async (id) => {
            try { await axios.delete(`${crmUrl}/scheduling/overrides/${id}`, { headers }); return true; } catch (err: any) { return handle(err); }
        },
        agenda: (f, t, userId) => get<IAgenda>(`/scheduling/agenda?${range(f, t)}${userId ? `&user_id=${userId}` : ''}`),
        team: () => get<ICalendarTeammate[]>('/scheduling/team'),
        createEvent: (input) => post<IAgendaEvent>('/calendar/events', input),
        updateEvent: async (calendarId, eventId, input) => {
            try {
                const r: AxiosResponse<IAgendaEvent> = await axios.patch(`${crmUrl}/calendar/events/${calendarId}/${encodeURIComponent(eventId)}`, input, { headers });
                return r.data;
            } catch (err: any) { return handle(err); }
        },
        deleteEvent: async (calendarId, eventId, notify) => {
            try { await axios.delete(`${crmUrl}/calendar/events/${calendarId}/${encodeURIComponent(eventId)}?notify=${notify}`, { headers }); return true; } catch (err: any) { return handle(err); }
        },
        slots: (typeId, f, t, exclude) => get<ISlots>(`/scheduling/slots?type=${typeId}&${range(f, t)}${exclude ? `&exclude=${exclude}` : ''}`),
        listAppointments: (f, t) => get<IAppointment[]>(`/scheduling/appointments?${range(f, t)}`),
        listForLead: (leadId) => get<IAppointment[]>(`/leads/${leadId}/appointments`),
        book: (input) => post<IAppointment>('/scheduling/appointments', input),
        cancel: (id, reason) => post<IAppointment>(`/scheduling/appointments/${id}/cancel`, { reason: reason ?? null }),
        reschedule: (id, startIso) => post<IAppointment>(`/scheduling/appointments/${id}/reschedule`, { start_at: startIso }),
        outcome: (id, status, sendFollowUp) => post<IAppointment>(`/scheduling/appointments/${id}/outcome`, { status, send_follow_up: sendFollowUp !== false }),
        confirmAppointment: (id) => post<IAppointment>(`/scheduling/appointments/${id}/confirm`, {}),
        notices: (id) => get<IAppointmentNotice[]>(`/scheduling/appointments/${id}/notices`),
        undoFollowUp: (id) => post<{ cancelled: number }>(`/scheduling/appointments/${id}/follow-up/undo`, {}),
        launchBot: (input) => post<IMeetingBot>('/meetings/bot', input),
        getBot: (id) => get<IMeetingBot>(`/meetings/bot/${id}`),
        botTranscript: (id) => get<IMeetingBotTranscript>(`/meetings/bot/${id}/transcript`),
        removeBot: async (id) => {
            try { const r: AxiosResponse<IMeetingBot> = await axios.delete(`${crmUrl}/meetings/bot/${id}`, { headers }); return r.data; } catch (err: any) { return handle(err); }
        },
        listBots: (appointmentId) => get<IMeetingBot[]>(`/scheduling/appointments/${appointmentId}/bots`),
        pendingWrapUps: () => get<IPendingWrapUp[]>('/meetings/wrapups'),
        completeWrapUp: (botId, submission) => post<IWrapUpResult>(`/meetings/bot/${botId}/wrapup`, submission),
        snoozeWrapUp: (botId, minutes) => post<IPendingWrapUp>(`/meetings/bot/${botId}/wrapup/snooze`, { minutes: minutes ?? 60 }),
        skipWrapUp: (botId) => post<IPendingWrapUp>(`/meetings/bot/${botId}/wrapup/skip`, {}),
        reanalyzeBot: (botId) => post<IMeetingBot>(`/meetings/bot/${botId}/reanalyze`, {}),
        demoWrapUps: () => get<IDemoWrapUp[]>('/demos/wrapups'),
        confirmDemo: (eventId, outcome) => post<{ updated: number }>(`/demos/${encodeURIComponent(eventId)}/outcome`, { outcome })
    }];
};
